import { useState, useEffect } from 'react';
import { apiFetch } from '../../lib/api';

// Sprint ACAD ACT.A8 (#1709) — shared shell for the two simple, informational
// first-login tours (STUDENT 5-step, FACULTY_SUPERVISOR 3-step). Q3
// adjudication: progress is server-side (survives logout), via
// GET/PATCH /api/onboarding/:flowType — NOT the legacy WizardShell's
// localStorage pattern (doesn't survive logout) and NOT ONB.C1's task-list
// setup_tasks (wrong shape — this is a linear numbered tour).
//
// The admin wizard (TENANT_ADMIN, 7 steps with real side effects — cohort/
// team creation, invites sent) does NOT use this shell; it's its own page
// (AcademicAdminOnboardingPage.tsx) since its steps are forms with real API
// calls, not static informational cards.

export type OnboardingFlowType = 'STUDENT' | 'FACULTY_SUPERVISOR';

export interface TourStep {
  title:    string;
  body:     React.ReactNode;
  cta?:     string;
}

interface OnboardingTourProps {
  flowType: OnboardingFlowType;
  steps:    TourStep[];
  onDone:   () => void;
}

interface ProgressResponse {
  currentStep: number;
  totalSteps:  number;
  completedAt: string | null;
}

export default function OnboardingTour({ flowType, steps, onDone }: OnboardingTourProps) {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [advancing, setAdvancing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiFetch<ProgressResponse>(`/api/onboarding/${flowType}`)
      .then((progress) => {
        if (cancelled) return;
        // Resume at the last INCOMPLETE step, not the completed one — a
        // completed flow that somehow lands back here (e.g. a stale
        // cached redirect) restarts the visible tour at its last step
        // rather than re-showing step 0.
        setStep(Math.min(progress.currentStep, steps.length - 1));
      })
      .catch(() => {
        // Fail open — start at step 0, same posture as LoginPage's
        // membership-fetch fallback.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowType]);

  const isLast = step === steps.length - 1;

  const handleNext = async () => {
    setAdvancing(true);
    try {
      const nextStep = isLast ? step : step + 1;
      await apiFetch(`/api/onboarding/${flowType}`, {
        method: 'PATCH',
        body: JSON.stringify({ currentStep: nextStep, completed: isLast }),
      });
      if (isLast) {
        onDone();
      } else {
        setStep(nextStep);
      }
    } catch {
      // Non-blocking — advance the visible step locally even if the
      // progress write failed, so the tour itself is never stuck.
      if (isLast) {
        onDone();
      } else {
        setStep((s) => Math.min(s + 1, steps.length - 1));
      }
    } finally {
      setAdvancing(false);
    }
  };

  const handleBack = () => setStep((s) => Math.max(0, s - 1));

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading…</div>;
  }

  const current = steps[step];

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg)' }}>
      <div style={{
        width: '100%',
        maxWidth: 480,
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-xl)',
        boxShadow: 'var(--shadow-lg)',
        padding: 40,
      }}>
        <div className="flex items-center justify-center mb-6" style={{ gap: 8 }}>
          {steps.map((_, i) => (
            <div
              key={i}
              data-testid={`tour-progress-dot-${i}`}
              style={{
                width: 32,
                height: 4,
                borderRadius: 2,
                background: i <= step ? 'var(--primary)' : 'var(--border)',
                transition: 'background 200ms',
              }}
            />
          ))}
        </div>

        <h2 style={{
          fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700,
          color: 'var(--text)', marginBottom: 8, textAlign: 'center',
        }}>
          {current.title}
        </h2>
        <div style={{
          fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)',
          textAlign: 'center', marginBottom: 28,
        }}>
          {current.body}
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          {step > 0 && (
            <button
              onClick={handleBack}
              disabled={advancing}
              style={{
                flex: '0 0 auto', height: 44, padding: '0 20px',
                background: 'transparent', color: 'var(--text-secondary)',
                border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 500,
                cursor: advancing ? 'not-allowed' : 'pointer',
              }}
            >
              Back
            </button>
          )}
          <button
            onClick={handleNext}
            disabled={advancing}
            style={{
              flex: 1, height: 44,
              background: 'var(--primary)', color: 'white',
              fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600,
              borderRadius: 'var(--radius-sm)', border: 'none',
              cursor: advancing ? 'not-allowed' : 'pointer',
              opacity: advancing ? 0.7 : 1,
            }}
          >
            {current.cta ?? (isLast ? 'Done' : 'Next')}
          </button>
        </div>
      </div>
    </div>
  );
}
